import { Bell, BellOff, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const PushNotificationToggle = () => {
  const { supported, permission, requestPermission, sendNotification } = usePushNotifications();
  const { toast } = useToast();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const handleToggle = async () => {
    if (permission === "granted") {
      toast({ title: "Notifications Active", description: "You're already receiving push alerts." });
      return;
    }
    const result = await requestPermission();
    if (result === "granted") {
      toast({ title: "Notifications Enabled", description: "You'll receive alerts for critical events." });
      sendNotification("GCM Alerts Active", { body: "You'll now receive real-time conflict alerts." });
    } else {
      toast({ title: "Notifications Blocked", description: "Please enable notifications in your browser settings.", variant: "destructive" });
    }
  };

  // Subscribe to realtime events for push notifications — deferred to avoid Lighthouse WebSocket errors
  useEffect(() => {
    if (permission !== "granted") return;

    const timer = setTimeout(() => {
      channelRef.current = supabase
        .channel("push-critical-events")
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "events" },
          (payload) => {
            const event = payload.new as any;
            if (["missile", "air_raid", "explosion"].includes(event.event_type)) {
              sendNotification(
                `⚠️ ${event.event_type.toUpperCase()}: ${event.title}`,
                {
                  body: `📍 ${event.city || event.country || "Unknown location"}\n🕐 ${new Date(event.occurred_at).toLocaleString()}`,
                  tag: event.id,
                  requireInteraction: true,
                }
              );
            }
          }
        )
        .subscribe();
    }, 5000);

    return () => {
      clearTimeout(timer);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [permission, sendNotification]);

  if (!supported) return null;

  const Icon = permission === "granted" ? BellRing : permission === "denied" ? BellOff : Bell;

  return (
    <Button
      variant="outline"
      size="sm"
      className={`gap-1.5 text-xs ${permission === "granted" ? "border-success/30 text-success" : ""}`}
      onClick={handleToggle}
      title={permission === "granted" ? "Push alerts active" : "Enable push alerts"}
    >
      <Icon className="h-3 w-3" />
      <span className="hidden sm:inline">{permission === "granted" ? "Alerts On" : "Alerts"}</span>
    </Button>
  );
};

export default PushNotificationToggle;
